import { FormlyFieldConfig } from '@ngx-formly/core';
import { AddressHint, ChildHint, PersonHint } from './hint.model';
import {
  addressHintToModel,
  birthGroupFromHint,
  childHintToRow,
  fillOrAppendFormlyArrayItem,
  findFieldByKey,
  isChildRowEmpty,
  patchFieldModel,
} from './hint.utils';

/** Low-level builder for a `hint-fill` formly field. Prefer the specific helpers below. */
export function hintFillField(config: {
  hintKind: 'address' | 'child' | 'person';
  apply: (field: FormlyFieldConfig, hint: AddressHint | ChildHint | PersonHint) => void;
  className?: string;
}): FormlyFieldConfig {
  return {
    type: 'hint-fill',
    className: config.className ?? 'p-col-12',
    props: {
      hintKind: config.hintKind,
      hintApply: config.apply,
    },
  };
}

/**
 * "Fill address from history" button.
 *
 * By default it patches its enclosing address group — place it as the first entry of an address
 * `fieldGroup` (e.g. `[hintFillAddressField(), ...sharedAddress()]`). Pass `targetKey` to instead
 * patch a named address control on the parent group (e.g. `{ targetKey: 'permanentniAdresa' }`).
 */
export function hintFillAddressField(opts: {
  targetKey?: string;
  className?: string;
} = {}): FormlyFieldConfig {
  return hintFillField({
    hintKind: 'address',
    className: opts.className,
    apply: (field, hint) => {
      const target = opts.targetKey
        ? field.parent?.formControl?.get(opts.targetKey)
        : field.parent?.formControl;
      target?.patchValue(addressHintToModel(hint as AddressHint));
    },
  });
}

/**
 * "Fill children from history" button. Fills the first empty child row of a sibling `deti` repeat
 * (or appends one) found under the same parent group.
 */
export function hintFillChildField(className?: string): FormlyFieldConfig {
  return hintFillField({
    hintKind: 'child',
    className,
    apply: (field, hint) =>
      fillOrAppendFormlyArrayItem(
        findFieldByKey(field.parent?.fieldGroup, 'deti'),
        childHintToRow(hint as ChildHint),
        isChildRowEmpty
      ),
  });
}

/**
 * Per-row "fill this child from history" button — place it as the first entry of a `deti` row's
 * panel (see `kid()` / `kid2026()`). Unlike {@link hintFillChildField} it never adds a row: it
 * overwrites the enclosing row at its concrete index, keeping any `uplatneni` months already
 * entered on that row.
 */
export function hintFillChildRowField(className?: string): FormlyFieldConfig {
  return hintFillField({
    hintKind: 'child',
    className,
    apply: (field, hint) => {
      const row = childHintToRow(hint as ChildHint);
      const uplatneni = (field.parent?.model as { uplatneni?: unknown[] } | undefined)?.uplatneni;
      if (Array.isArray(uplatneni) && uplatneni.length) {
        delete row.uplatneni;
      }
      patchFieldModel(field.parent, row);
    },
  });
}

/**
 * "Fill this person from history" for an ATS-style person group whose identity fields are the plain
 * keys `jmeno` / `prijmeni` / `rodneCisloADatumNarozeni` (e.g. a loan co-debtor row). Fills the
 * enclosing group in place — seed the model + rebuild so the (gated) birth controls hydrate.
 * Persons no longer carry an address; use a separate {@link hintFillAddressField} for that.
 */
export function hintFillAtsPersonField(className?: string): FormlyFieldConfig {
  return hintFillField({
    hintKind: 'person',
    className,
    apply: (field, hint) => {
      const person = hint as PersonHint;
      patchFieldModel(field.parent, {
        jmeno: person.jmeno ?? undefined,
        prijmeni: person.prijmeni ?? undefined,
        rodneCisloADatumNarozeni: birthGroupFromHint(person),
      });
    },
  });
}

/**
 * "Fill another taxpayer from history" button for the declaration's `partner` group. Place it inside
 * the `dalsiPoplatnik`-gated group, right above the taxpayer name fields it fills — `partnerForm()`
 * does that for you via its `personHint` flag. The picked person's identity is written onto the
 * taxpayer sub-fields; the address is filled separately via {@link hintFillAddressField}.
 */
export function hintFillPartnerField(className?: string): FormlyFieldConfig {
  return hintFillField({
    hintKind: 'person',
    className,
    apply: (field, hint) => {
      const person = hint as PersonHint;
      // `field.parent` is the (keyless) gated group, so its model is the partner group's. Seed the
      // model and rebuild instead of patching the form — the birth sub-group's controls are gated
      // behind `ceskeRodneCislo` (hidden → unregistered), so a plain patchValue would drop them.
      patchFieldModel(field.parent, {
        jmenoPoplatnika: person.jmeno ?? undefined,
        prijmeniPoplatnika: person.prijmeni ?? undefined,
        rodneCisloADatumNarozeniPoplatnika: birthGroupFromHint(person),
      });
    },
  });
}
